import {
  INTRANET_AI_AGENT_TEMPLATES,
  type IntranetAiAgent,
  type IntranetAiAgentTemplateId,
  type IntranetAiAgentWrite,
} from '@gb-ticket/shared-types';
import { Loader2Icon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ApiRequestError } from '@/lib/api';
import {
  useIntranetAiAgents,
  useReplaceIntranetAiAgents,
} from '@/platform/features/ai/use-ai-agents';
import {
  useIntranetCategoryPosterRoles,
  useIntranetContents,
} from '@/products/intranet/features/intranet/use-intranet';

type AgentDraft = IntranetAiAgentWrite;

function toDraft(agent: IntranetAiAgent): AgentDraft {
  return {
    id: agent.id,
    name: agent.name,
    description: agent.description,
    instruction: agent.instruction,
    enabled: agent.enabled,
    roleIds: [...agent.roleIds],
    contentIds: [...agent.contentIds],
  };
}

function emptyAgent(): AgentDraft {
  const template = INTRANET_AI_AGENT_TEMPLATES.find((item) => item.id === 'leer');
  return {
    name: template?.name ?? 'Neuer Agent',
    description: template?.description ?? '',
    instruction: template?.instruction ?? '',
    enabled: true,
    roleIds: [],
    contentIds: [],
  };
}

export function AiAdminAgentsBlock() {
  const agentsQuery = useIntranetAiAgents();
  const replace = useReplaceIntranetAiAgents();
  const roles = useIntranetCategoryPosterRoles();
  const library = useIntranetContents(
    { section: 'library', drafts: true, status: 'published', page: 1, pageSize: 50 },
    true,
  );

  if (agentsQuery.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Agenten werden geladen …
      </p>
    );
  }

  if (agentsQuery.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {agentsQuery.error instanceof ApiRequestError
          ? agentsQuery.error.message
          : 'Die Agenten konnten nicht geladen werden.'}
      </p>
    );
  }

  if (!agentsQuery.data) {
    return null;
  }

  const listKey = agentsQuery.data.items.map((item) => item.updatedAt).join('|') || 'empty';

  return (
    <AiAdminAgentsForm
      key={listKey}
      initialAgents={agentsQuery.data.items.map(toDraft)}
      roleOptions={roles.data?.items ?? []}
      fileOptions={(library.data?.items ?? []).filter((item) => item.currentFile !== null)}
      saving={replace.isPending}
      onSave={async (agents) => {
        try {
          await replace.mutateAsync({ agents });
          toast.success('Die Agenten wurden gespeichert.');
        } catch (error) {
          toast.error(
            error instanceof ApiRequestError
              ? error.message
              : 'Die Agenten wurden nicht gespeichert.',
          );
        }
      }}
    />
  );
}

function AiAdminAgentsForm({
  initialAgents,
  roleOptions,
  fileOptions,
  saving,
  onSave,
}: {
  initialAgents: AgentDraft[];
  roleOptions: { id: string; name: string; code: string }[];
  fileOptions: { id: string; title: string; currentFile: { filename: string } | null }[];
  saving: boolean;
  onSave: (agents: AgentDraft[]) => Promise<void>;
}) {
  const [agents, setAgents] = useState(initialAgents);
  const [templateId, setTemplateId] = useState<IntranetAiAgentTemplateId>('handbuch');
  const selectedTemplate = INTRANET_AI_AGENT_TEMPLATES.find((item) => item.id === templateId);

  function patchAgent(index: number, patch: Partial<AgentDraft>) {
    setAgents((current) =>
      current.map((agent, agentIndex) => (agentIndex === index ? { ...agent, ...patch } : agent)),
    );
  }

  function toggleId(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
  }

  function applyTemplate(index: number) {
    const template = INTRANET_AI_AGENT_TEMPLATES.find((item) => item.id === templateId);
    if (!template) {
      return;
    }
    patchAgent(index, {
      name: template.name,
      description: template.description,
      instruction: template.instruction,
    });
  }

  function addFromTemplate() {
    const template = INTRANET_AI_AGENT_TEMPLATES.find((item) => item.id === templateId);
    if (!template) {
      return;
    }
    setAgents((current) => [
      ...current,
      {
        name: template.name,
        description: template.description,
        instruction: template.instruction,
        enabled: true,
        roleIds: [],
        contentIds: [],
      },
    ]);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Agenten</CardTitle>
          <CardDescription>
            Benannte Assistenten für den Intranet-KI-Chat. Dateien und Rollen legen fest, wer
            welchen Agenten sieht.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>Zuerst das Recht „Intranet-KI benutzen“ an die Rollen geben, die fragen sollen.</p>
          <p>
            Dann einen Agenten anlegen, eine Vorlage übernehmen und nur veröffentlichte Seiten
            auswählen, die diese Personen sehen dürfen. Eine Seite kann mehrere Dokumente enthalten.
            Der Agent liest alle aktuellen Dateien dieser Seite.
          </p>
          <p>
            Die Personen finden den Agenten auf der Seite KI im Intranet. Ohne passende Rolle bleibt
            er unsichtbar.
          </p>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[12rem] flex-1">
          <Label htmlFor="ai-agent-template">Vorlage</Label>
          <Select
            value={templateId}
            disabled={saving}
            onValueChange={(value) => setTemplateId(value as IntranetAiAgentTemplateId)}
          >
            <SelectTrigger id="ai-agent-template" className="mt-2 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTRANET_AI_AGENT_TEMPLATES.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedTemplate ? (
            <p className="mt-2 text-sm text-muted-foreground">{selectedTemplate.description}</p>
          ) : null}
        </div>
        <Button type="button" variant="outline" disabled={saving} onClick={() => addFromTemplate()}>
          <PlusIcon className="size-4" aria-hidden />
          Agent aus Vorlage
        </Button>
      </div>

      {agents.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Agenten angelegt.</p>
      ) : (
        <ul className="space-y-6">
          {agents.map((agent, index) => (
            <li key={agent.id ?? `new-${index}`}>
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                  <div>
                    <CardTitle className="text-base">{agent.name || 'Agent'}</CardTitle>
                    <CardDescription>
                      Ein Speichern ersetzt die gesamte Agenten-Liste.
                    </CardDescription>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={saving}
                      onClick={() => applyTemplate(index)}
                    >
                      Vorlage übernehmen
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={saving}
                      aria-label="Agent entfernen"
                      onClick={() => setAgents((current) => current.filter((_, i) => i !== index))}
                    >
                      <Trash2Icon className="size-4" aria-hidden />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-1">
                      <Label htmlFor={`agent-enabled-${index}`}>Aktiv</Label>
                      <p className="text-sm text-muted-foreground">
                        Inaktive Agenten erscheinen nicht im Chat.
                      </p>
                    </div>
                    <Switch
                      id={`agent-enabled-${index}`}
                      checked={agent.enabled}
                      disabled={saving}
                      onCheckedChange={(enabled) => patchAgent(index, { enabled })}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`agent-name-${index}`}>Name</Label>
                    <Input
                      id={`agent-name-${index}`}
                      className="mt-2"
                      value={agent.name}
                      disabled={saving}
                      onChange={(event) => patchAgent(index, { name: event.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`agent-description-${index}`}>Beschreibung</Label>
                    <Input
                      id={`agent-description-${index}`}
                      className="mt-2"
                      value={agent.description}
                      disabled={saving}
                      onChange={(event) => patchAgent(index, { description: event.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`agent-instruction-${index}`}>Anweisung</Label>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Nur Ton und Schwerpunkt. Antworten kommen ausschließlich aus den verknüpften
                      Beiträgen, nicht aus dem eigenen Wissen des Modells.
                    </p>
                    <Textarea
                      id={`agent-instruction-${index}`}
                      className="mt-2 min-h-24"
                      value={agent.instruction}
                      disabled={saving}
                      onChange={(event) => patchAgent(index, { instruction: event.target.value })}
                    />
                  </div>
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Berechtigungsgruppen</legend>
                    <ul className="max-h-40 space-y-2 overflow-y-auto rounded-xl border border-border bg-card p-3 shadow-sm">
                      {roleOptions.map((role) => (
                        <li key={role.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`agent-${index}-role-${role.id}`}
                            checked={agent.roleIds.includes(role.id)}
                            disabled={saving}
                            onCheckedChange={() =>
                              patchAgent(index, { roleIds: toggleId(agent.roleIds, role.id) })
                            }
                          />
                          <Label htmlFor={`agent-${index}-role-${role.id}`} className="font-normal">
                            {role.name}
                          </Label>
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Veröffentlichte Seiten</legend>
                    <p className="text-sm text-muted-foreground">
                      Angezeigt wird ein Dateiname. Der Agent liest jede aktuelle Datei der Seite.
                    </p>
                    <ul className="max-h-48 space-y-2 overflow-y-auto rounded-xl border border-border bg-card p-3 shadow-sm">
                      {fileOptions.map((entry) => (
                        <li key={entry.id} className="flex items-start gap-2">
                          <Checkbox
                            id={`agent-${index}-file-${entry.id}`}
                            checked={agent.contentIds.includes(entry.id)}
                            disabled={saving}
                            onCheckedChange={() =>
                              patchAgent(index, {
                                contentIds: toggleId(agent.contentIds, entry.id),
                              })
                            }
                          />
                          <Label
                            htmlFor={`agent-${index}-file-${entry.id}`}
                            className="leading-snug font-normal"
                          >
                            {entry.title}
                            {entry.currentFile ? (
                              <span className="block text-xs text-muted-foreground">
                                {entry.currentFile.filename}
                              </span>
                            ) : null}
                          </Label>
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={saving}
          onClick={() => setAgents((current) => [...current, emptyAgent()])}
        >
          <PlusIcon className="size-4" aria-hidden />
          Leerer Agent
        </Button>
        <Button type="button" disabled={saving} onClick={() => void onSave(agents)}>
          {saving && <Loader2Icon className="animate-spin" aria-hidden />}
          Agenten speichern
        </Button>
      </div>
    </div>
  );
}
